import React from "react";
import Modal from "../common/modal";
import UploadImages from "../upoloadimages";
import Button from "../common/button";
import Toggle from "../common/toggle";
import { useFormik } from "formik";
import { getBrandValidationSchema } from "../../validation/brandValidation";
import { resolveImageUrl } from "../../utils/resolveImageUrl";

const toBool = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (["true", "1", "active", "enabled", "yes"].includes(s)) return true;
    if (["false", "0", "inactive", "disabled", "no"].includes(s)) return false;
  }
  return false;
};

const emptyValues = {
  title: "",
  slug: "",
  description: "",
  isActive: true,
  image: null,
};

const BrandModal = ({
  isOpen,
  onClose,
  editData,
  viewMode = false,
  mode = "create",
  onSubmit,
  isLoading = false,
}) => {
  const isEditing = mode === "edit";
  const isDelete = mode === "delete";
  const hasExistingImage = !!(editData?.image?.url || editData?.image);

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    initialValues: editData
      ? {
          title: editData.title || "",
          slug: editData.slug || "",
          description: editData.description || "",
          isActive: toBool(
            typeof editData.isActive !== "undefined"
              ? editData.isActive
              : editData.status,
          ),
          image: null,
        }
      : emptyValues,
    validationSchema: getBrandValidationSchema(isEditing, hasExistingImage),
    onSubmit: (values) => {
      onSubmit({
        title: values.title,
        slug: values.slug,
        description: values.description,
        isActive: values.isActive,
        image: values.image,
      });
    },
  });

  const handleImageChange = (file) => {
    formik.setFieldValue("image", file);
    formik.setFieldTouched("image", true, false);
    formik.validateField("image");
  };

  const handleSave = () => {
    formik.setTouched({
      title: true,
      slug: true,
      description: true,
      image: true,
    });
    formik.handleSubmit();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isDelete
          ? "Delete Brand"
          : viewMode
            ? "View Brand"
            : isEditing
              ? "Edit Brand"
              : "Add Brand"
      }
    >
      {isDelete ? (
        <>
          <div style={{ padding: "10px 2px" }}>
            <p style={{ margin: 0, color: "#333" }}>
              Are you sure you want to delete{" "}
              <strong>{editData?.title || "this brand"}</strong>?
            </p>
            <p style={{ margin: "8px 0 0", color: "#666", fontSize: 13 }}>
              This action will remove it from the database and the table.
            </p>
          </div>

          <div className="modal-footer">
            <Button onClick={onClose}>Cancel</Button>
            <Button onClick={() => onSubmit?.()} disabled={isLoading}>
              {isLoading ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </>
      ) : (
        <form onSubmit={formik.handleSubmit}>
          <div className="form-container">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                name="title"
                placeholder="Brand Name"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={viewMode}
              />
              {formik.touched.title && formik.errors.title && (
                <p className="error-text">{formik.errors.title}</p>
              )}
            </div>

            <div className="form-group">
              <label>Slug</label>
              <input
                type="text"
                name="slug"
                placeholder="brand-slug"
                value={formik.values.slug}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={viewMode}
              />
              {formik.touched.slug && formik.errors.slug && (
                <p className="error-text">{formik.errors.slug}</p>
              )}
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                placeholder="Brand Description"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                disabled={viewMode}
              />
              {formik.touched.description && formik.errors.description && (
                <p className="error-text">{formik.errors.description}</p>
              )}
            </div>

            <div
              className="form-group"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 30,
              }}
            >
              <label>Status</label>
              <div className="sharing-toggle">
                <Toggle
                  onChange={(checked) =>
                    formik.setFieldValue("isActive", checked)
                  }
                  checked={!!formik.values.isActive}
                  disabled={viewMode}
                />
              </div>
            </div>

            <div
              className="form-group"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                marginTop: 20,
              }}
            >
              <label>Brand Image</label>

              {viewMode ? (
                (() => {
                  const imgSrc = editData?.image?.url || editData?.image;
                  if (!imgSrc) return <div>No Image</div>;
                  const fullSrc = resolveImageUrl(imgSrc);
                  return (
                    <img
                      src={fullSrc}
                      alt="brand"
                      style={{
                        width: 120,
                        height: 120,
                        objectFit: "cover",
                        borderRadius: 10,
                      }}
                    />
                  );
                })()
              ) : (
                <div
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {isEditing &&
                    (editData?.image?.url || editData?.image) &&
                    !formik.values.image && (
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                        }}
                      >
                        {(() => {
                          const imgSrc =
                            editData?.image?.url || editData?.image;
                          const fullSrc = resolveImageUrl(imgSrc);
                          return (
                            <img
                              src={fullSrc}
                              alt="Current"
                              style={{
                                width: 80,
                                height: 80,
                                objectFit: "cover",
                                borderRadius: 8,
                                border: "1px solid #ddd",
                              }}
                            />
                          );
                        })()}
                      </div>
                    )}
                  <UploadImages
                    showTitle={false}
                    onChange={handleImageChange}
                  />
                  {formik.touched.image && formik.errors.image && (
                    <p className="error-text">{formik.errors.image}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="modal-footer">
            <Button type="button" className="CancelButton" onClick={onClose}>
              {viewMode ? "Close" : "Cancel"}
            </Button>

            {!viewMode && (
              <Button
                type="button"
                className="CommonButton"
                onClick={handleSave}
                disabled={isLoading}
              >
                {isLoading
                  ? "Saving..."
                  : isEditing
                    ? "Save Changes"
                    : "Add Brand"}
              </Button>
            )}
          </div>
        </form>
      )}
    </Modal>
  );
};

export default BrandModal;
